import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout, Spinner } from '@/components/Layout';
import { useAuth } from '@/lib/auth';
import { Landing } from '@/pages/Landing';
import { SignIn } from '@/pages/SignIn';
import { Join } from '@/pages/Join';
import { Home } from '@/pages/Home';
import { SearchPage } from '@/pages/SearchPage';
import { PersonPage } from '@/pages/PersonPage';
import { Desk } from '@/pages/Desk';
import { PrintCards } from '@/pages/PrintCards';
import { MemberCodePage } from '@/pages/MemberCodePage';
import { Events } from '@/pages/Events';
import { Dojos } from '@/pages/Dojos';
import { Renew } from '@/pages/Renew';
import { CheckoutReturn } from '@/pages/CheckoutReturn';
import { BookEvent } from '@/pages/BookEvent';
import { EventPage } from '@/pages/EventPage';
import { EnterGrading } from '@/pages/EnterGrading';
import { MyDojo } from '@/pages/MyDojo';
import { GradingSheets } from '@/pages/admin/GradingSheets';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { AdminEvents } from '@/pages/admin/AdminEvents';
import { AdminEventEdit } from '@/pages/admin/AdminEventEdit';
import { AdminMembers } from '@/pages/admin/AdminMembers';
import { AdminHousehold } from '@/pages/admin/AdminHousehold';
import { AdminPerson } from '@/pages/admin/AdminPerson';
import { AdminCertificates } from '@/pages/admin/AdminCertificates';
import { AdminPayments } from '@/pages/admin/AdminPayments';
import { AdminPayment } from '@/pages/admin/AdminPayment';
import { AdminEmail, AdminEmailBatch } from '@/pages/admin/AdminEmail';
import { AdminEmailCompose } from '@/pages/admin/AdminEmailCompose';
import { DojoEmail, DojoEmailSent } from '@/pages/DojoEmail';
import { AdminDojo, AdminDojos } from '@/pages/admin/AdminDojos';
import { AdminInstructors } from '@/components/admin/Instructors';
import { History } from '@/components/admin/History';

function SignedIn({ children }: { children: JSX.Element }) {
  const { session, loading } = useAuth();
  if (loading) return <Spinner />;
  return session ? children : <Navigate to="/signin" replace />;
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<Landing />} />
        <Route path="signin" element={<SignIn />} />
        <Route path="events" element={<Events />} />
        <Route path="events/:slug" element={<EventPage />} />
        <Route path="dojos" element={<Dojos />} />
        <Route path="join" element={<SignedIn><Join /></SignedIn>} />
        <Route path="home" element={<SignedIn><Home /></SignedIn>} />
        <Route path="search" element={<SignedIn><SearchPage /></SignedIn>} />
        <Route path="people/:id" element={<SignedIn><PersonPage /></SignedIn>} />
        <Route path="m/:code" element={<SignedIn><MemberCodePage /></SignedIn>} />
        <Route path="renew" element={<SignedIn><Renew /></SignedIn>} />
        <Route path="checkout/return" element={<SignedIn><CheckoutReturn /></SignedIn>} />
        <Route path="events/:id/book" element={<SignedIn><BookEvent /></SignedIn>} />
        <Route path="events/:id/grading" element={<SignedIn><EnterGrading /></SignedIn>} />
        <Route path="dojo" element={<SignedIn><MyDojo /></SignedIn>} />
        <Route path="dojo/:dojoId" element={<SignedIn><MyDojo /></SignedIn>} />
        <Route path="dojo/email" element={<SignedIn><DojoEmail /></SignedIn>} />
        <Route path="dojo/email/sent/:id" element={<SignedIn><DojoEmailSent /></SignedIn>} />
        <Route path="admin" element={<SignedIn><AdminLayout /></SignedIn>}>
          <Route index element={<Navigate to="events" replace />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="events/:id" element={<AdminEventEdit />} />
          <Route path="members" element={<AdminMembers />} />
          <Route path="households/:id" element={<AdminHousehold />} />
          <Route path="people/:id" element={<AdminPerson />} />
          <Route path="dojos" element={<AdminDojos />} />
          <Route path="dojos/:id" element={<AdminDojo />} />
          <Route path="instructors" element={<AdminInstructors />} />
          <Route path="certificates" element={<AdminCertificates />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="payments/:id" element={<AdminPayment />} />
          <Route path="email" element={<AdminEmail />} />
          <Route path="email/new" element={<AdminEmailCompose />} />
          <Route path="email/sent/:id" element={<AdminEmailBatch />} />
          <Route path="history" element={<History title="Recent changes" limit={300} />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
      {/* Printable pages sit outside the site layout so they print clean. */}
      <Route path="admin/events/:id/sheets" element={<SignedIn><GradingSheets /></SignedIn>} />
      <Route path="events/:id/desk" element={<SignedIn><Desk /></SignedIn>} />
      <Route path="cards" element={<SignedIn><PrintCards /></SignedIn>} />
    </Routes>
  );
}
